import "./style-modern.css";
import axios from "axios";

// Type definitions for modern form elements
interface ModernFormElements {
  sendmessage: HTMLButtonElement;
  name: HTMLInputElement;
  contact: HTMLInputElement;
  message: HTMLTextAreaElement;
  error: HTMLElement;
  success: HTMLElement;
}

interface ModernMenuElements {
  menuBtn: HTMLElement;
  sideMenu: HTMLElement;
  overlay: HTMLElement;
  menuClose: HTMLElement;
  header: HTMLElement;
}

// Global dataLayer type
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Form validation functions
const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const validatePhone = (phone: string): boolean => {
  const regex = /^(\+254|0)[0-9]{9}$/;
  return regex.test(phone);
};

// Get contact form elements with type safety
const getContactFormElements = (): ModernFormElements => {
  const sendmessage = document.getElementById(
    "sendmessage",
  ) as HTMLButtonElement;
  const name = document.getElementById("c-name") as HTMLInputElement;
  const contact = document.getElementById("c-contact") as HTMLInputElement;
  const message = document.getElementById("c-message") as HTMLTextAreaElement;
  const error = document.getElementById("error-contact") as HTMLElement;
  const success = document.getElementById("success-contact") as HTMLElement;

  if (!sendmessage || !name || !contact || !message || !error || !success) {
    throw new Error("Required contact form elements not found");
  }

  return { sendmessage, name, contact, message, error, success };
};

const getMenuElements = (): ModernMenuElements => {
  const menuBtn = document.getElementById("menu-btn") as HTMLElement;
  const sideMenu = document.getElementById("side-menu") as HTMLElement;
  const overlay = document.getElementById("overlay") as HTMLElement;
  const menuClose = document.getElementById("menu-close") as HTMLElement;
  const header = document.getElementById("header") as HTMLElement;

  if (!menuBtn || !sideMenu || !overlay || !menuClose || !header) {
    throw new Error("Required menu elements not found");
  }

  return { menuBtn, sideMenu, overlay, menuClose, header };
};

// Contact form handler with modern validation
const setupContactForm = (): void => {
  try {
    const elements = getContactFormElements();

    elements.sendmessage.addEventListener("click", async (e: Event) => {
      e.preventDefault();
      elements.error.style.display = "none";
      elements.success.style.display = "none";

      if (elements.sendmessage.innerHTML === "Sending Message...") {
        return;
      }

      // Google Analytics tracking
      const dataLayer = window.dataLayer || [];
      const event = {
        event: "go-duka-contact",
        item: {},
      };
      dataLayer.push(event);

      // Form validation
      let isValid = true;
      let errorMessage = "";

      if (elements.name.value.trim() === "") {
        isValid = false;
        errorMessage = "Please provide your name";
      } else if (elements.contact.value.trim() === "") {
        isValid = false;
        errorMessage = "Please provide an email or phone number";
      } else if (
        !validateEmail(elements.contact.value) &&
        !validatePhone(elements.contact.value)
      ) {
        isValid = false;
        errorMessage = "Please provide a valid email or phone number";
      } else if (elements.message.value.trim() === "") {
        isValid = false;
        errorMessage = "Please provide a message";
      }

      if (!isValid) {
        elements.error.innerHTML = errorMessage;
        elements.error.style.display = "block";
        return;
      }

      elements.sendmessage.innerHTML = "Sending Message...";
      elements.sendmessage.disabled = true;

      try {
        const res = await axios.post(
          "http://localhost:3001/business/zidika/message",
          {
            name: elements.name.value.trim(),
            contact: elements.contact.value.trim(),
            message: elements.message.value.trim(),
          },
        );

        if (res.data.success) {
          elements.success.style.display = "block";
          elements.sendmessage.style.display = "none";
          elements.error.style.display = "none";

          // Reset form
          elements.name.value = "";
          elements.contact.value = "";
          elements.message.value = "";
        } else {
          throw new Error("Server error");
        }
      } catch (error) {
        elements.error.innerHTML = "Unable to send message. Please try again.";
        elements.error.style.display = "block";
        elements.sendmessage.innerHTML = "Send Message";
        elements.sendmessage.disabled = false;
      }
    });
  } catch (error) {
    console.error("Error setting up contact form:", error);
  }
};

// Modern menu functionality
const setupMenu = (): void => {
  try {
    const elements = getMenuElements();

    // Mobile menu toggle
    elements.menuBtn.addEventListener("click", (e: Event) => {
      e.preventDefault();
      elements.sideMenu.classList.toggle("open");
      elements.overlay.style.display = elements.sideMenu.classList.contains(
        "open",
      )
        ? "block"
        : "none";
      document.body.style.overflow = elements.sideMenu.classList.contains(
        "open",
      )
        ? "hidden"
        : "auto";
    });

    // Close menu
    elements.menuClose.addEventListener("click", (e: Event) => {
      e.preventDefault();
      closeMenu();
    });

    // Close menu when clicking overlay
    elements.overlay.addEventListener("click", (e: Event) => {
      e.preventDefault();
      closeMenu();
    });

    // Close menu when clicking nav links
    const navLinks = elements.sideMenu.querySelectorAll(".mobile-nav-link");
    navLinks.forEach((link) => {
      link.addEventListener("click", () => {
        closeMenu();
      });
    });
  } catch (error) {
    console.error("Error setting up menu:", error);
  }
};

// Global closeMenu function
declare global {
  function closeMenu(): void;
}

const closeMenu = (): void => {
  try {
    const sideMenu = document.getElementById("side-menu") as HTMLElement;
    const overlay = document.getElementById("overlay") as HTMLElement;

    if (sideMenu && overlay) {
      sideMenu.classList.remove("open");
      overlay.style.display = "none";
      document.body.style.overflow = "auto";
    }
  } catch (error) {
    console.error("Error closing menu:", error);
  }
};

// Make closeMenu globally available
(window as any).closeMenu = closeMenu;

// Header scroll effect
const setupHeaderScroll = (): void => {
  const header = document.getElementById("header") as HTMLElement;
  if (!header) return;

  let lastScrollY = window.scrollY;

  window.addEventListener("scroll", () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 100) {
      header.classList.add("scrolled");
    } else {
      header.classList.remove("scrolled");
    }

    lastScrollY = currentScrollY;
  });
};

// Smooth scrolling for anchor links
const setupSmoothScrolling = (): void => {
  const links = document.querySelectorAll('a[href^="#"]');

  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (!href || href === "#") return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      const headerHeight = 80; // Approximate header height
      const targetPosition =
        target.getBoundingClientRect().top + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    });
  });
};

// Loading screen management
const setupLoadingScreen = (): void => {
  const loading = document.getElementById("loading") as HTMLElement;
  const app = document.getElementById("app") as HTMLElement;

  if (!loading || !app) return;

  // Hide loading screen when page is fully loaded
  window.addEventListener("load", () => {
    setTimeout(() => {
      loading.classList.add("hidden");
      app.style.display = "block";

      // Remove loading screen from DOM after animation
      setTimeout(() => {
        loading.style.display = "none";
      }, 500);
    }, 1000); // Minimum loading time for better UX
  });
};

// Intersection Observer for animations
const setupScrollAnimations = (): void => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px",
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("animate-in");
      }
    });
  }, observerOptions);

  // Observe elements for animation
  const animateElements = document.querySelectorAll(
    ".benefit-card, .testimonial-card, .about-card",
  );
  animateElements.forEach((el) => {
    observer.observe(el);
  });
};

// Initialize all functionality when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  setupContactForm();
  setupMenu();
  setupHeaderScroll();
  setupSmoothScrolling();
  setupLoadingScreen();
  setupScrollAnimations();
});
